import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { partnerSettingsKeys } from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import {
  getPartnerAnalyticsRangeParams,
  mapPartnerRevenueAnalyticsToChartPoints,
} from "@/components/pages/guesteria-partners/utils/partnerAnalyticsPeriod";
import type { PartnerRevenueDateRange } from "@/components/pages/guesteria-partners/utils/partnerRevenuePeriodPresets";
import { useHotel } from "@/hooks/useHotel";
import { partnerSettingsService } from "@/services/partner-settings-service";
import type {
  PartnerRevenueAnalyticsApiResponse,
  PartnerRevenueAnalyticsBucket,
  PartnerRevenueAnalyticsChartPoint,
} from "@/types/partner-revenue-analytics";
import type { PartnerProviderType } from "@/types/partner-settings";
import { is404 } from "@/utils/httpErrors";

const emptyAnalytics = (
  bucket: PartnerRevenueAnalyticsApiResponse["bucket"],
): PartnerRevenueAnalyticsApiResponse => ({
  bucket,
  points: [],
});

// Task 0038 (owner, 2026-09-30): `range` is the shared period (future
// inclusive), `granularity` is the separate "Group by" control. The chart
// itself is clamped at today inside `getPartnerAnalyticsRangeParams` — see
// that function's comment for why (a static "Expected" caption, not greyed
// future buckets).
export const usePartnerRevenueAnalytics = (
  range: PartnerRevenueDateRange,
  granularity: PartnerRevenueAnalyticsBucket,
  provider: PartnerProviderType | undefined,
  enabled = true,
) => {
  const { hotelId } = useHotel();
  const { i18n } = useTranslation();
  const periodParams = useMemo(
    () => getPartnerAnalyticsRangeParams(range, granularity),
    [range, granularity],
  );
  const periodKey = `${periodParams.from}_${periodParams.to}_${periodParams.bucket}`;

  const query = useQuery<PartnerRevenueAnalyticsApiResponse>({
    queryKey: partnerSettingsKeys.revenueAnalytics(
      hotelId || "",
      periodKey,
      provider || "",
    ),
    // `provider` is only present for SUPER_ADMIN/GUESTERIA_ADMIN viewers
    // (tasks 0028/0029, contract 2026-09-28) — gating on it broke this query
    // for TENANT_ADMIN/HOTEL_ADMIN/FRONT_DESK, who never have it set.
    enabled: Boolean(hotelId) && enabled,
    queryFn: async () => {
      try {
        return await partnerSettingsService.getPartnerRevenueAnalytics(
          hotelId!,
          {
            ...periodParams,
            provider,
          },
        );
      } catch (e) {
        if (is404(e)) {
          return emptyAnalytics(periodParams.bucket);
        }
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });

  const chartPoints = useMemo<PartnerRevenueAnalyticsChartPoint[]>(() => {
    if (!query.data) {
      return [];
    }
    return mapPartnerRevenueAnalyticsToChartPoints(
      query.data,
      i18n.language || "en",
      i18n.t,
    );
  }, [query.data, i18n]);

  const currency =
    query.data?.points.find((point) => point.currency)?.currency ?? "EUR";

  return {
    ...query,
    chartPoints,
    currency,
    bucket: query.data?.bucket,
    periodParams,
  };
};
